import { DialogContent } from '@mui/material'

import {
  ConfigurableProperty,
  DigitalTwin,
  DigitalTwinSettingCreationRequest,
  useDigitalTwin,
} from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingLevel, DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { BASE_SETTING_PRIORITIES } from 'api/digitalTwin/digitalTwinConstants'
import { getDeviationSettingPriorities } from 'helpers/settingPriority.helper/settingPriority.helper'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { Dialog } from 'ui/components/Dialog/Dialog'
import PreviousSettingsTable from 'ui/components/DigitalTwinEditSettingsModal/PreviousSettingsTable/PreviousSettingsTable'
import { DigitalTwinSettingDialog } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialog'
import { CreateSettingData } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import { useConfirmedClose } from 'ui/components/DigitalTwinSettingEditor/useConfirmedClose'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import SelectButton from 'ui/components/SelectButton/SelectButton'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import { getPropertiesFromDigitalTwinModel } from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import {
  getPrettyName,
  getSettingsWithUnitsFromMatchingConfigurableProperty,
} from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import {
  EventChangeRequest,
  isTimeSeriesSettingInstruction,
  SettingOrTimeSeriesInstructions,
} from 'views/EventsView/components/events.helper'
import styles from './EventSettingsOverviewTable.module.less'

export type SettingsInstructionsData = DigitalTwinSettingCreationRequest & { delete?: boolean; id: number }
export type ChangeRequestSettingsOverviewTableProps = {
  level?: string | undefined
  attribute?: string
  name?: string
  index: number
  changeRequest?: EventChangeRequest
  settings?: SettingOrTimeSeriesInstructions[]
  disabled?: boolean
  setSettingInstructionsData?: (data: SettingsInstructionsData) => void
}

// NOTE: If you make changes to this file it should probebly be changed in EventSettingInstructionOverviewTable.tsx as well
export default function ChangeRequestSettingsInstructionsOverviewTable({
  level,
  attribute,
  name,
  index,
  changeRequest,
  settings,
  disabled,
  setSettingInstructionsData,
}: ChangeRequestSettingsOverviewTableProps): ReactElement {
  const { t } = useTranslation()
  const { activeSystem, hasAccess, user } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)
  const [openCreateSettingModal, setOpenCreateSettingModal] = useState(false)
  const [openEditSettingModal, setOpenEditSettingModal] = useState(false)

  const [initData, setInitData] = useState<null | CreateSettingData>(null)
  const closeSettingModal = (): void => {
    setInitData(null)
    openCreateSettingModal ? setOpenCreateSettingModal(false) : setOpenEditSettingModal(false)
  }
  const { requestClose, onUnsavedChangesChange, closeConfirmDialog } = useConfirmedClose(closeSettingModal)
  const priorities = useMemo(() => getDeviationSettingPriorities(activeSystem?.setting_priority_levels), [activeSystem])

  const canUpdateActiveEvent = changeRequest?.permitted_actions.includes('update')
  const noSettingWritePermission = changeRequest?.setting_instructions?.linked_objects?.[index]?.write === false
  const disableSettings = !canUpdateActiveEvent || noSettingWritePermission || disabled

  const userName = `${user?.first_name} ${user?.last_name}`

  const eventId = changeRequest?.operational_event_id

  const onClickSelectButtonForSetting = useCallback(() => {
    setInitData({
      level: level as DeviationSettingLevel,
      name: name,
      attribute: attribute,
      start_time: changeRequest?.start_time,
      end_time: changeRequest?.end_time,
      priority: priorities[0].priority_level,
      isTimeSeries: false,
      operational_event: eventId ?? null,
      creator_name: userName ?? undefined,
    })
    setOpenCreateSettingModal(true)
  }, [attribute, changeRequest?.end_time, eventId, changeRequest?.start_time, level, name, priorities, userName])

  const onClickSelectButtonForTimeSeriesSetting = useCallback(() => {
    setInitData({
      level: level as DeviationSettingLevel,
      name: name,
      attribute: attribute,
      start_time: changeRequest?.start_time,
      end_time: changeRequest?.end_time,
      value: [],
      priority: priorities[0].priority_level,
      isTimeSeries: true,
      operational_event: eventId ?? null,
      creator_name: userName ?? undefined,
    })
    setOpenCreateSettingModal(true)
  }, [attribute, changeRequest?.end_time, eventId, changeRequest?.start_time, level, name, priorities, userName])

  const settingsForChosenPropertyWithUnits = getSettingsWithUnitsFromMatchingConfigurableProperty(
    digitalTwin,
    settings ?? []
  )
  const hasAccessToTimeseries = hasAccess({ submodule: 'optimizesettings_time_series', module: 'events' })

  const onEditSettingInstruction = useCallback(
    (setting: SettingOrTimeSeriesInstructions | DigitalTwinSetting): void => {
      const isTimeSeries = isTimeSeriesSettingInstruction(setting)
      const value = isTimeSeries
        ? 'values' in setting
          ? setting.values?.map((value: number | null) => ({ value: value }))
          : []
        : 'value' in setting
          ? typeof setting.value === 'string'
            ? Number(setting.value)
            : setting.value
          : null

      setInitData({
        level: setting.level as DeviationSettingLevel,
        name: setting.name,
        attribute: setting.attribute,
        priority: setting.priority,
        comment: setting.comment,
        value: value,
        isTimeSeries: isTimeSeries,
        operational_event: eventId ?? null,
        id: setting.id ?? undefined,
        creator_name: setting.creator_name ?? undefined,
        start_time: (setting.start_time as `${number}-${number}-${number}T${number}:${number}:${number}+00:00`) ?? null,
        end_time: (setting.end_time as `${number}-${number}-${number}T${number}:${number}:${number}+00:00`) ?? null,
      })
      setOpenEditSettingModal(true)
    },
    [eventId]
  )

  const allConfigurableProperties: ConfigurableProperty[] = useMemo(
    () => (digitalTwin?.model ? getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model) : []),
    [digitalTwin]
  )

  const disableCreateSettingButton =
    level === '' ||
    name === '' ||
    attribute === '' ||
    level === undefined ||
    name === undefined ||
    attribute === undefined

  return (
    <>
      <div className={styles.EventSettingsOverviewTable_CreateSettingsButton}>
        {disableSettings ? (
          <></>
        ) : hasAccess({ submodule: 'optimizesettings_time_series', module: 'events' }) ? (
          <SelectButton
            disabled={disableCreateSettingButton}
            icon="fal fa-plus"
            primary
            menuItems={[
              {
                label: t('Setting'),
                onClick: onClickSelectButtonForSetting,
              },
              {
                label: t('Time series setting'),
                onClick: onClickSelectButtonForTimeSeriesSetting,
              },
            ]}
          >
            {t(`Create new setting`)}
          </SelectButton>
        ) : (
          <Button primary onClick={onClickSelectButtonForSetting} disabled={disableCreateSettingButton}>
            {t(`Create new setting`)}
          </Button>
        )}
      </div>
      <PreviousSettingsTable
        allSettingsForChosenProperty={settingsForChosenPropertyWithUnits}
        onEditSetting={onEditSettingInstruction}
        permissions={{
          //Set always to true so user is able to view the setting (good to see e.g. graph for timeseries settings). All fields are disabled if user doesn't have permission
          canEdit: true,
          canEditHistoric: true,
        }}
        priorities={priorities}
        eventPost={{
          isOperationalEvent: true,
          eventId: eventId ?? null,
          linked_objects: changeRequest?.setting_instructions?.linked_objects ?? null,
          index: index,
        }}
      />
      <Dialog open={openCreateSettingModal || openEditSettingModal} fullWidth maxWidth="md">
        <CustomDialogTitle
          title={`${getPrettyName(`${level}.${name}`, digitalTwin?.translations)}: ${getPrettyName(`${level}.${name}.${attribute}`, digitalTwin?.translations)}`}
          handleClose={requestClose}
        />
        <DialogContent>
          <div>
            {initData?.isTimeSeries && !hasAccessToTimeseries && (
              <InfoBanner text={t('Unfortunately, you do not have permission to make changes here')} style="warning" />
            )}
            <DigitalTwinSettingDialog
              propertyItems={settings ?? []}
              digitalTwin={digitalTwin ?? ({} as DigitalTwin)}
              allowEmpty={false}
              priorities={priorities}
              baseSettingPriorities={BASE_SETTING_PRIORITIES}
              operationalEvent={changeRequest}
              initData={initData}
              configurableProperties={allConfigurableProperties}
              isBaseSettings={false}
              hasUnitAndProperty={true}
              disable={disableSettings}
              onClose={closeSettingModal}
              onUnsavedChangesChange={onUnsavedChangesChange}
              onRequestClose={requestClose}
              setSettingInstructionsData={setSettingInstructionsData}
            />
          </div>
        </DialogContent>
        {closeConfirmDialog}
      </Dialog>
    </>
  )
}
